import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { RefreshCw } from 'lucide-react'
import type {
  PropertyReservationsResponse,
  PropertyThreadsPageResponse,
} from '@natalan/capehost-contracts'
import {
  usePropertyThreadsInfinite,
  usePropertyReservations,
  useReservation,
  useProperty,
} from '@/hooks/useApi'
import { useThreadOrchestration } from '@/hooks/useThreadOrchestration'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { ConversationList } from '@/components/messages/conversation-list'
import { ThreadPane } from '@/components/messages/thread-pane'
import { ThreadHeader } from '@/components/messages/thread-header'
import { ReservationSidebar } from '@/components/messages/reservation-sidebar'
import { Composer } from '@/components/messages/composer'
import { CalendarTab } from '@/components/property/calendar-tab'
import { PropertyHeader } from '@/components/property/property-header'
import { PropertyPageTabList } from '@/components/property/property-page-tabs'
import { propertyTabPath } from '@/lib/property-routes'
import { Tabs } from '@/components/ui/tabs'
import { getReservationLifecycleStatus, getReservationStayDates } from '@/lib/reservation-utils'
import { getGuestDisplayName } from '@/lib/format-utils'
import { cn } from '@/lib/utils'

type ViewMode = 'list' | 'calendar'

/** A single reservation row as returned by GET /admin/properties/:id/reservations. */
type ReservationListItem = PropertyReservationsResponse['reservations'][number]

/** A single row as returned by GET /admin/properties/:id/threads/page. */
type ThreadPageItem = PropertyThreadsPageResponse['items'][number]

/**
 * Cross-component "shape I need" adapters. ThreadHeader / ReservationSidebar /
 * CalendarTab / format-utils each independently declare their own narrower
 * local reservation/property shape (e.g. `check_in?: string`, no `| null`)
 * that predates this page's real contract-typed API data - those files are
 * each already converted and stable, out of scope for this conversion.
 * Extracting each callee's own declared parameter/prop type (rather than
 * duplicating or loosening it) and casting this page's real API values
 * against it at the boundary keeps this page's own state/variables typed
 * against the real contract shapes without touching those files.
 */
type ThreadHeaderReservationProp = Parameters<typeof ThreadHeader>[0]['reservation']
type ReservationSidebarReservationProp = Parameters<typeof ReservationSidebar>[0]['reservation']
type CalendarTabReservationsProp = Parameters<typeof CalendarTab>[0]['reservations']
type CalendarTabReservationItem = NonNullable<CalendarTabReservationsProp>[number]
type GuestDisplayNameArg = Parameters<typeof getGuestDisplayName>[0]

/**
 * "Shape I need" for a clicked conversation-list row. conversation-list.tsx
 * exports a narrower ThreadListItem type scoped to its own internal
 * rendering, but the actual row object (sourced from the same paginated
 * threads contract type this page's own `listItems` use) also carries
 * `propertyId`/`reservationId`, which this page reads from the clicked row
 * to seed the header/sidebar immediately, before the newly-selected
 * thread's own fetch has resolved.
 */
interface ThreadListSelection {
  threadId?: string
  propertyId?: string | null
  reservationId?: string | null
  guest?: { name?: string | null; email?: string | null } | null
}

function getReservationSortTime(
  reservation: Parameters<typeof getReservationStayDates>[0],
  key: 'checkIn' | 'checkOut'
): number {
  const { checkIn, checkOut } = getReservationStayDates(reservation)
  const value = key === 'checkOut' ? checkOut : checkIn
  const time = value ? new Date(value).getTime() : NaN
  return Number.isNaN(time) ? 0 : time
}

/**
 * Picks which reservation to show in the always-populated sidebar when
 * there is no thread to derive it from (the "no-threads-yet" fallback -
 * see "Resolved decisions" #2 in docs/archive/messages-redesign-pr-slices.md).
 * Prefers the soonest active/upcoming stay (current stay first), falling
 * back to the most recently completed past stay - mirrors the "active"
 * filter's own sort in property-details.jsx.
 */
function pickDefaultReservation(
  reservations: ReservationListItem[] | null | undefined
): ReservationListItem | null {
  if (!reservations || reservations.length === 0) return null

  const withStatus = reservations.map(r => ({ r, status: getReservationLifecycleStatus(r) }))

  const upcoming = withStatus
    .filter(x => x.status === 'active' || x.status === 'upcoming')
    .sort((a, b) => {
      const aActive = a.status === 'active'
      const bActive = b.status === 'active'
      if (aActive !== bActive) return aActive ? -1 : 1
      return getReservationSortTime(a.r, 'checkIn') - getReservationSortTime(b.r, 'checkIn')
    })
  if (upcoming.length > 0) return upcoming[0].r

  const past = withStatus
    .filter(x => x.status === 'past')
    .sort(
      (a, b) => getReservationSortTime(b.r, 'checkOut') - getReservationSortTime(a.r, 'checkOut')
    )
  if (past.length > 0) return past[0].r

  return reservations[0]
}

interface ReservationIdLike {
  id?: string
  reservation_id?: string
}

/**
 * Reservation shapes seen across this page (thread payloads, the plain
 * `/reservations` listing, calendar bars) inconsistently use `id` vs
 * `reservation_id` - mirrors the identical fallback in calendar-tab.jsx's
 * own (unexported) getReservationId.
 */
function getReservationIdForLookup(reservation?: ReservationIdLike | null): string | null {
  return reservation?.id || reservation?.reservation_id || null
}

/**
 * List/Calendar segmented control - switches the SAME inbox route between
 * the three-pane list view and the calendar view (docs/messages-redesign-
 * plan.md's "Calendar view" section; a Codex pre-review on the plan doc
 * caught an earlier draft wiring the calendar into property-details.jsx as
 * a separate tab instead, which this avoids).
 */
function ViewModeToggle({
  value,
  onChange,
}: {
  value: ViewMode
  onChange: (mode: ViewMode) => void
}) {
  const options: { key: ViewMode; label: string }[] = [
    // "Threads" and "Stays" rather than "List" and "Calendar" (CAP-1034 mock):
    // the property tab row right under this header has its own Calendar tab.
    { key: 'list', label: 'Threads' },
    { key: 'calendar', label: 'Stays' },
  ]
  return (
    <div
      role="group"
      aria-label="View mode"
      className="inline-flex items-center gap-0.5 rounded-md border border-border p-0.5"
    >
      {options.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className={cn(
            'rounded px-2.5 py-1 text-xs font-medium transition-colors',
            value === key ? 'bg-surface2 text-text' : 'text-textMuted hover:text-text'
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

interface PageHeaderProps {
  propertyId: string
  property?: { archived?: boolean | null } | null
  isPropertyLoading: boolean
  isSyncing: boolean
  lastSyncAt?: string | null
  onSync?: () => void
  viewMode?: ViewMode
  onViewModeChange?: (mode: ViewMode) => void
}

/**
 * The shared property header (CAP-1034) with this page's own controls on its
 * right, then the property tab row with Messages selected, so moving between
 * the property page and Messages keeps both rows where they were.
 *
 * Reservations already sync via the nightly admin-api cron and Uplisting
 * webhooks (root CLAUDE.md, "Uplisting Sync Rules") - the "Sync reservations"
 * control (demoted to a small status control per
 * docs/archive/messages-redesign-plan.md's "Sync reservations button" section)
 * is for "I just changed something in Uplisting and want it reflected
 * immediately," not a routine action.
 */
function PageHeader({
  propertyId,
  property,
  isPropertyLoading,
  isSyncing,
  lastSyncAt,
  onSync,
  viewMode,
  onViewModeChange,
}: PageHeaderProps) {
  const navigate = useNavigate()
  const handleTabChange = (value: string) => {
    // Messages is already showing; every other tab is on the property page.
    if (value === 'messages') return
    navigate(propertyTabPath(propertyId, value))
  }
  return (
    <>
      <PropertyHeader
        propertyId={propertyId}
        property={property}
        isPropertyLoading={isPropertyLoading}
        actions={
          <>
            {viewMode && onViewModeChange && (
              <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
            )}
            {onSync && (
              <div className="flex items-center gap-2 text-xs text-textMuted">
                {lastSyncAt && (
                  <span>
                    Synced {formatDistanceToNow(new Date(lastSyncAt), { addSuffix: true })}
                  </span>
                )}
                <Button variant="secondary" size="sm" onClick={onSync} disabled={isSyncing}>
                  <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Syncing…' : 'Sync reservations'}
                </Button>
              </div>
            )}
          </>
        }
      />
      <Tabs value="messages" onValueChange={handleTabChange} className="mb-4">
        <PropertyPageTabList />
      </Tabs>
    </>
  )
}

/**
 * Every state of this page is a column exactly as tall as the space under the
 * top bar (64px) and the layout's padding (16px a side, 24px from md), so the
 * three panes take whatever the header leaves, including when its actions wrap
 * onto a second row on a narrow screen.
 */
const PAGE_ROOT_CLASS = 'flex h-[calc(100vh-96px)] flex-col md:h-[calc(100vh-112px)]'

function FullPageSkeleton() {
  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[320px_1fr_320px]">
      <LoadingSkeleton className="h-full w-full" />
      <LoadingSkeleton className="h-full w-full" />
      <LoadingSkeleton className="h-full w-full" />
    </div>
  )
}

/**
 * Standalone, deep-linkable Messages inbox for a property - assembles the
 * conversation list, thread pane, reservation sidebar, and composer built
 * in earlier Wave 2 slices (docs/archive/messages-redesign-pr-slices.md, "Wire the
 * three panes together + delete the eager fetch").
 *
 * Handles both `/properties/:propertyId/messages` (defaults to the most
 * recent conversation, or the no-threads/empty-property fallbacks below)
 * and `/properties/:propertyId/messages/:threadId` (deep link to a
 * specific thread, fetched directly via useThreadMessages rather than
 * depending on it being present in the paginated list - a thread older
 * than the first page would not have loaded there).
 */
export function PropertyMessagesPage() {
  // Keyed by property: the header's picker switches property on this same
  // route (CAP-1034), and every piece of page state below (view mode,
  // calendar selection, previews, sync time) belongs to one property, so a
  // switch starts the page fresh rather than carrying any of it over.
  const { propertyId } = useParams<{ propertyId: string }>()
  return <PropertyMessagesPageContent key={propertyId} />
}

function PropertyMessagesPageContent() {
  const { propertyId, threadId } = useParams<{ propertyId: string; threadId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Same query key/args ConversationList uses internally by default (no
  // limit, needsReply=false) - this shares its cache entry rather than
  // issuing a second request, and gives us the most-recent thread for the
  // no-threadId default-selection redirect below.
  const listQuery = usePropertyThreadsInfinite({ propertyId })
  const listItems = useMemo<ThreadPageItem[]>(
    () => listQuery.data?.pages.flatMap(page => page.items) ?? [],
    [listQuery.data]
  )
  const mostRecentThreadId = listItems[0]?.threadId ?? null

  // Best-effort "needs reply" set for the calendar view's dot indicator
  // (CalendarTab) - derived from whatever pages of the conversation list
  // are already loaded, not a separate fetch. Same pagination-bounded
  // caveat as the conversation list's own "Needs reply" filter.
  const needsReplyReservationIds = useMemo(() => {
    const ids = new Set<string>()
    for (const item of listItems) {
      if (item.needsReply && item.reservationId) ids.add(item.reservationId)
    }
    return ids
  }, [listItems])

  const propertyQuery = useProperty(propertyId)

  // Guards handleSelectReservationFromCalendar's async continuation below
  // (Codex PR review) - kept true while this page instance is mounted AND
  // still showing the same property the click was started for, false
  // otherwise. Without this, an operator who clicks a calendar bar and then
  // navigates away (or to a different property) before the thread lookup
  // resolves would get yanked back to this page by a stale navigate() call
  // firing after they'd already left.
  //
  // useLayoutEffect, not useEffect (a further Codex PR review): passive
  // effects are deferred until after the browser paints, so there is a real
  // (if narrow) window after a new route commits but before a passive
  // effect runs where a lookup's fetch could resolve and its continuation
  // would still read the OLD propertyId/token here, passing both guards.
  // Layout effects flush synchronously as part of the same commit, closing
  // that window entirely.
  const isActiveForPropertyRef = useRef<string | null | undefined>(propertyId)
  useLayoutEffect(() => {
    isActiveForPropertyRef.current = propertyId
    return () => {
      isActiveForPropertyRef.current = null
    }
  }, [propertyId])

  // Bumped by handleViewModeChange below every time the operator manually
  // switches views (Codex PR review) - lets
  // handleSelectReservationFromCalendar's async continuation tell whether
  // it's still resolving for the view the operator is actually looking at,
  // separately from the property-liveness check above.
  const calendarResolutionTokenRef = useRef(0)
  // Also bumped on ANY route change under this same component instance -
  // not just the manual view-mode toggle above (a further Codex PR review):
  // browser Back/Forward between this property's base and thread routes
  // changes `threadId` without changing `propertyId` or touching the
  // segmented control, so a slow lookup started before that navigation
  // would otherwise still pass both existing guards and call navigate()
  // over the operator's newer, already-completed navigation. A ref mutation
  // (not a setState call), so this doesn't trigger an extra render or the
  // react-hooks/set-state-in-effect rule. useLayoutEffect for the same
  // synchronous-timing reason as isActiveForPropertyRef above.
  useLayoutEffect(() => {
    calendarResolutionTokenRef.current += 1
  }, [propertyId, threadId])

  // List/Calendar segmented control (Wave 3 step 4) - a view-mode toggle
  // within this SAME route, not a separate property-details tab. Local UI
  // state only, not reflected in the URL.
  const [viewMode, setViewMode] = useState<ViewMode>('list')

  // Captured straight from the conversation-list row the operator clicked,
  // so the header/sidebar have a reservation to look up immediately -
  // before threadQuery has even resolved the newly-selected thread. Without
  // this, the reservation sidebar had to wait on threadData.reservation_id,
  // which only exists once the thread fetch completes, even though the
  // clicked row already carried its own reservationId. Matched against both
  // threadId AND propertyId below (not threadId alone) so a stale preview
  // can never leak across a property switch.
  const [selectedThreadPreview, setSelectedThreadPreview] = useState<ThreadListSelection | null>(
    null
  )

  // Set when a calendar bar was clicked for a reservation that turned out
  // to have NO thread yet (handleSelectReservationFromCalendar below) -
  // overrides the no-threads-yet fallback's own pickDefaultReservation()
  // selection so the sidebar reflects the reservation the operator actually
  // clicked, not just "most recent/upcoming". Cleared whenever the operator
  // picks a real thread from the conversation list instead.
  const [calendarSelection, setCalendarSelection] = useState<CalendarTabReservationItem | null>(
    null
  )

  // Reservation ID currently being checked for an existing thread (Wave 3
  // step 3's "resolve via GET .../thread, not the loaded list state") -
  // drives a brief loading indicator so a bar click never looks
  // unresponsive while that request is in flight.
  const [resolvingReservationId, setResolvingReservationId] = useState<string | null>(null)

  // Both of the above are scoped to a single property (Codex PR review):
  // React Router can reuse this same component instance across a propertyId
  // change (same route pattern, different :propertyId param, no remount), so
  // without this a threadless calendarSelection from property A would keep
  // rendering property A's reservation on property B, and a
  // resolvingReservationId left over from an in-flight lookup that started
  // before the navigation would never clear (its own `finally` guard - see
  // handleSelectReservationFromCalendar below - correctly refuses to act on
  // a now-stale property, but that also means nothing else clears it),
  // permanently disabling further calendar clicks. Reset calendarSelection
  // whenever propertyId changes, and resolvingReservationId whenever EITHER
  // propertyId or threadId changes - a further Codex PR review caught that
  // the token-bump effect above (which also fires on a threadId-only
  // change, e.g. browser Back/Forward between this property's base and
  // thread routes) correctly invalidates a pending lookup's *result*, but
  // never itself clears resolvingReservationId, so the same stuck-spinner/
  // blocked-clicks bug from the propertyId case could still happen via
  // threadId alone. Done during render (the React-docs "adjusting state
  // when a prop changes" pattern), not in a useEffect, so this doesn't
  // trigger an extra post-commit render pass or the
  // react-hooks/set-state-in-effect rule.
  const [routeKeyForCalendarState, setRouteKeyForCalendarState] = useState<{
    propertyId?: string
    threadId?: string
  }>({
    propertyId,
    threadId,
  })
  if (
    propertyId !== routeKeyForCalendarState.propertyId ||
    threadId !== routeKeyForCalendarState.threadId
  ) {
    if (propertyId !== routeKeyForCalendarState.propertyId) {
      setCalendarSelection(null)
    }
    setResolvingReservationId(null)
    setRouteKeyForCalendarState({ propertyId, threadId })
  }

  const {
    threadQuery,
    threadData,
    propertyMismatch,
    messages,
    setThreadScrollNode,
    liveMessage,
    suggestion,
    isGenerating,
    isPollExhausted,
    isSendConfirmationExhausted,
    hasActivePendingSendForLiveMessage,
    isSubmittingGenerate,
    isDismissingNeedsReply,
    handleSend,
    handleResendPendingMessage,
    lastInlineResendSentKey,
    handleRegenerate,
    handleCheckAgain,
    handleDismissNeedsReply,
  } = useThreadOrchestration({
    threadId,
    expectedPropertyId: propertyId,
    listQueryKeyPrefix: ['property-threads-page', propertyId],
  })

  useEffect(() => {
    if (propertyMismatch) {
      toast({
        title: 'Conversation not found',
        description: "That conversation doesn't belong to this property.",
        variant: 'destructive',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyMismatch])

  const previewMatchesSelection =
    selectedThreadPreview?.threadId === threadId && selectedThreadPreview?.propertyId === propertyId
  const previewReservationId = previewMatchesSelection
    ? (selectedThreadPreview?.reservationId ?? null)
    : null
  const reservationIdFromThread = !propertyMismatch
    ? (threadData?.reservation_id ?? previewReservationId ?? null)
    : null
  const reservationFromThreadQuery = useReservation(reservationIdFromThread)

  // Only needed once we know the list's first page has nothing to select
  // and we're not viewing a specific deep-linked thread - avoids an
  // unnecessary reservations fetch on every normal thread view. Not needed
  // (for the "which reservation is the fallback default" question) when
  // calendarSelection is already set - that came straight from the clicked
  // calendar bar. The query itself is still kept enabled in that case
  // below, though - see the comment on `enabled`.
  const needsFallbackCheck =
    !threadId && !listQuery.isLoading && listItems.length === 0 && !calendarSelection
  // Also enabled for the calendar view itself (Wave 3 step 2/4) - the same
  // usePropertyReservations hook the Reservations tab table already uses,
  // reused here rather than inventing a second one - AND whenever
  // calendarSelection is set (a further Codex PR review): "Sync
  // reservations" calls reservationsQuery.refetch(), and without this
  // staying enabled here the query wouldn't even be fetching, so a sync
  // could never actually refresh the sidebar showing a calendar-selected
  // threadless reservation - see the refreshedCalendarSelection derivation
  // below, which is what actually picks up the refetched data.
  const reservationsQuery = usePropertyReservations(propertyId, {
    enabled: needsFallbackCheck || viewMode === 'calendar' || !!calendarSelection,
  })

  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null)

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      const result = await api.syncPropertyReservations(propertyId ?? '')
      if (result.ok && result.lastSuccessfulSyncAt) {
        setLastSyncAt(result.lastSuccessfulSyncAt)
      }
      await Promise.all([
        reservationsQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ['property-threads-page', propertyId] }),
        // The always-populated sidebar on a :threadId route is fed by this
        // SEPARATE query (useReservation(reservationIdFromThread)), not by
        // reservationsQuery above (that one only powers the no-threads
        // fallback) - without this, a sync that imports changed Uplisting
        // fields (dates, door code, status, notes) left the sidebar showing
        // stale cached data until something unrelated refetched it or the
        // page remounted (Codex PR review).
        reservationIdFromThread
          ? queryClient.invalidateQueries({ queryKey: ['reservation', reservationIdFromThread] })
          : Promise.resolve(),
      ])
      toast({
        title: 'Sync completed',
        description: `Synced ${result.synced || 0} reservation${result.synced !== 1 ? 's' : ''}`,
      })
    } catch (error) {
      toast({
        title: 'Sync failed',
        description:
          error instanceof Error ? error.message : 'Failed to sync reservations from Uplisting',
        variant: 'destructive',
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const handleSelectThread = (item: ThreadListSelection) => {
    if (!item?.threadId) return
    setCalendarSelection(null)
    setSelectedThreadPreview(item)
    navigate(`/properties/${propertyId}/messages/${item.threadId}`)
  }

  const handleViewModeChange = (mode: ViewMode) => {
    if (mode === viewMode) return
    // Invalidates any calendar-bar-click lookup still in flight (Codex PR
    // review) - the operator manually choosing a view is a newer, more
    // authoritative decision than a resolution that was already stale by
    // the time it settles; handleSelectReservationFromCalendar's own
    // internal setViewMode('list') calls (its success/no-thread paths)
    // don't go through this function, so they never bump this themselves.
    calendarResolutionTokenRef.current += 1
    // The abandoned lookup's own `finally` below now correctly refuses to
    // clear this (its token no longer matches - a further Codex PR review),
    // so nothing else will ever clear it once bumped here. Without this,
    // a manual view switch away from Calendar while a lookup is in flight
    // would leave resolvingReservationId stuck non-null forever, silently
    // disabling every future calendar-bar click via the guard clause below.
    setResolvingReservationId(null)
    setViewMode(mode)
    if (mode === 'calendar') {
      // Fresh entry into the calendar always starts from a clean slate -
      // any earlier no-thread fallback selection shouldn't resurface once
      // the operator comes back to List some other way.
      setCalendarSelection(null)
    }
  }

  // Bar click -> reservation + thread jump (Wave 3 step 3). Resolves
  // whether a thread exists for the clicked reservation via the existing
  // GET /admin/reservations/:reservationId/thread endpoint
  // (api.getReservationThread, client.js:214), NOT from the paginated
  // conversation-list state - a Codex pre-review on the plan doc caught
  // that the reservation's thread could easily be older than what's
  // currently loaded in the list, so inferring "no thread" from loaded
  // pages alone would be wrong.
  const handleSelectReservationFromCalendar = async (reservation: CalendarTabReservationItem) => {
    const reservationId = getReservationIdForLookup(reservation)
    if (!reservationId || resolvingReservationId) return
    const startedForPropertyId = propertyId
    const startedToken = calendarResolutionTokenRef.current
    setResolvingReservationId(reservationId)
    try {
      const result = await api.getReservationThread(reservationId)
      // Bail out if either: the operator navigated away from this property
      // (or unmounted the page entirely), or - a further Codex PR review -
      // manually switched back to List view (via the segmented control)
      // before this lookup settled. Either way, a stale result must never
      // drive navigation or selection state at this point.
      if (
        isActiveForPropertyRef.current !== startedForPropertyId ||
        calendarResolutionTokenRef.current !== startedToken
      ) {
        return
      }
      const resolvedThreadId = result?.thread?.threadId ?? null
      if (resolvedThreadId) {
        setCalendarSelection(null)
        setViewMode('list')
        navigate(`/properties/${startedForPropertyId}/messages/${resolvedThreadId}`)
      } else {
        // No thread yet - reuse the exact same no-threads-yet fallback the
        // base route already renders (sidebar populated from the
        // reservation, thread pane in its own empty state), just reached
        // via a calendar-bar click instead of the default route load.
        setCalendarSelection(reservation)
        setViewMode('list')
        if (threadId) {
          navigate(`/properties/${startedForPropertyId}/messages`)
        }
      }
    } catch (error) {
      if (
        isActiveForPropertyRef.current !== startedForPropertyId ||
        calendarResolutionTokenRef.current !== startedToken
      ) {
        return
      }
      toast({
        title: 'Could not check for an existing conversation',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      })
    } finally {
      // Also requires the token to still match (Codex PR review): without
      // it, an OLDER superseded lookup (e.g. one started on this same
      // property before the operator navigated away and back, then started
      // a newer lookup here before the first one settled) would reach this
      // `finally` with the property check alone passing, and clear the
      // NEWER lookup's resolvingReservationId - dropping its progress
      // indicator and re-enabling calendar clicks while it's still active.
      // Each invocation may only ever clear the state it itself owns.
      if (
        isActiveForPropertyRef.current === startedForPropertyId &&
        calendarResolutionTokenRef.current === startedToken
      ) {
        setResolvingReservationId(null)
      }
    }
  }

  const pageHeader = (
    <PageHeader
      propertyId={propertyId ?? ''}
      property={propertyQuery.data}
      isPropertyLoading={propertyQuery.isLoading}
      isSyncing={isSyncing}
      lastSyncAt={lastSyncAt}
      onSync={handleSync}
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
    />
  )

  // --- Calendar view: List/Calendar segmented control (Wave 3 step 4) ---
  // Renders in place of the three-pane layout regardless of whether the
  // route currently has a :threadId - it's a view-mode toggle on this same
  // page, not a separate route. All the hooks above keep running
  // (including threadQuery/listQuery) so switching back to List is instant.
  if (viewMode === 'calendar') {
    return (
      <div className={PAGE_ROOT_CLASS}>
        {pageHeader}
        {resolvingReservationId && (
          <div className="mb-3 flex items-center gap-2 text-xs text-textMuted">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            Checking for an existing conversation…
          </div>
        )}
        {reservationsQuery.isLoading ? (
          <LoadingSkeleton className="min-h-0 w-full flex-1" />
        ) : reservationsQuery.error ? (
          <ErrorState error={reservationsQuery.error} onRetry={reservationsQuery.refetch} />
        ) : (
          <CalendarTab
            propertyId={propertyId ?? ''}
            reservations={reservationsQuery.data?.reservations as CalendarTabReservationsProp}
            onSelectReservation={handleSelectReservationFromCalendar}
            needsReplyReservationIds={needsReplyReservationIds}
          />
        )}
      </div>
    )
  }

  // --- Deep-linked thread route ---
  if (threadId) {
    if (propertyMismatch) {
      return <Navigate to={`/properties/${propertyId}/messages`} replace />
    }

    // The three-pane shell (conversation list, header, sidebar) stays
    // mounted across a thread-to-thread navigation - only the message pane
    // below shows its own scoped loading/error state while threadQuery
    // fetches the newly-selected thread. An earlier version returned a
    // FullPageSkeleton/ErrorState here instead, which replaced this entire
    // tree (dropping ConversationList - along with its own internal "Needs
    // reply" filter selection - and blanking the header/sidebar) every time
    // an operator clicked a thread that wasn't already cached.
    const previewGuestName = previewMatchesSelection ? selectedThreadPreview?.guest?.name : null
    const guestDisplayName = reservationFromThreadQuery.data
      ? getGuestDisplayName(reservationFromThreadQuery.data as GuestDisplayNameArg)
      : previewGuestName || 'Guest'

    return (
      <div className={PAGE_ROOT_CLASS}>
        {pageHeader}
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[320px_1fr_320px]">
          <div className="overflow-hidden rounded-card border border-border bg-surface">
            <ConversationList
              propertyId={propertyId}
              selectedThreadId={threadId}
              onSelectThread={handleSelectThread}
            />
          </div>
          <div className="flex flex-col overflow-hidden rounded-card border border-border bg-surface">
            <ThreadHeader
              reservation={reservationFromThreadQuery.data as ThreadHeaderReservationProp}
              guestDisplayName={guestDisplayName}
            />
            <div
              ref={setThreadScrollNode}
              data-testid="thread-scroll-container"
              className="flex-1 overflow-y-auto p-4"
            >
              {threadQuery.isLoading ? (
                <div className="flex h-full items-center justify-center gap-2 text-sm text-textMuted">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Loading conversation…
                </div>
              ) : threadQuery.error ? (
                <ErrorState error={threadQuery.error} onRetry={threadQuery.refetch} />
              ) : (
                <ThreadPane
                  messages={messages}
                  guestDisplayName={guestDisplayName}
                  onResendPendingMessage={handleResendPendingMessage}
                  isSendConfirmationExhausted={isSendConfirmationExhausted}
                  onCheckAgainSendConfirmation={handleCheckAgain}
                />
              )}
            </div>
            {threadQuery.isLoading ? (
              // Composer stays unmounted (rather than rendered-but-disabled)
              // while the thread is still loading - a disabled textarea is
              // still exposed as an interactive textbox, so a
              // visible-but-not-yet-usable composer here would be
              // misleading for anyone who tries to type into it before the
              // thread is ready.
              <div className="border-t border-border p-3">
                <LoadingSkeleton className="h-20 w-full" />
              </div>
            ) : threadQuery.error ? (
              // Static, not animated (a further Codex PR review): the
              // fetch has already settled into an error state above, so an
              // animate-pulse skeleton here would misleadingly suggest the
              // composer is still loading rather than genuinely unavailable.
              <div className="border-t border-border p-3 text-center text-xs text-textFaint">
                Reply unavailable until the conversation loads
              </div>
            ) : (
              <>
                {liveMessage && (!suggestion || isGenerating || threadData?.needs_reply) && (
                  <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-xs text-textMuted">
                    <span>
                      {!suggestion || isGenerating
                        ? isPollExhausted
                          ? 'Still working on a reply - check again or try once more.'
                          : isGenerating
                            ? 'Generating a suggested reply…'
                            : 'No suggestion yet'
                        : null}
                    </span>
                    <div className="flex items-center gap-2">
                      {(!suggestion || isGenerating) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            isPollExhausted ? handleCheckAgain() : handleRegenerate(null)
                          }
                          disabled={(isGenerating && !isPollExhausted) || isSubmittingGenerate}
                        >
                          <RefreshCw
                            className={`h-3.5 w-3.5 ${isGenerating && !isPollExhausted ? 'animate-spin' : ''}`}
                          />
                          {isPollExhausted ? 'Check again' : 'Generate suggestion'}
                        </Button>
                      )}
                      {threadData?.needs_reply && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleDismissNeedsReply}
                          disabled={isDismissingNeedsReply}
                        >
                          {isDismissingNeedsReply ? 'Marking…' : 'No reply needed'}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <Composer
                  threadId={threadId}
                  suggestion={suggestion}
                  externallyResentIdempotencyKey={lastInlineResendSentKey}
                  onSend={handleSend}
                  onRegenerate={handleRegenerate}
                  disabled={!liveMessage || isGenerating || hasActivePendingSendForLiveMessage}
                />
              </>
            )}
          </div>
          <div className="overflow-y-auto rounded-card border border-border bg-surface p-4">
            {reservationFromThreadQuery.isLoading ||
            // useReservation(null) is disabled and never reports isLoading
            // (Codex PR review) - without this, opening an uncached thread
            // via a path that has no list-row preview to fall back on (a
            // direct URL, browser back/forward, or the calendar flow) left
            // reservationIdFromThread null until threadData itself
            // resolved, so this branch fell straight to the `else` below
            // and rendered ReservationSidebar with an undefined reservation
            // - showing misleading placeholder fields ("Guest", "Not set in
            // Uplisting") instead of a loading state.
            (!reservationIdFromThread && threadQuery.isLoading) ? (
              <LoadingSkeleton className="h-64 w-full" />
            ) : reservationFromThreadQuery.error ||
              // Same gap as the isLoading case above, for the failure path
              // (a further Codex PR review): if the thread request itself
              // fails on one of those same no-preview paths, reservationFromThreadQuery
              // stays disabled with no error of its own, so without this the
              // sidebar would render an undefined reservation next to the
              // conversation error already shown in the message pane.
              (!reservationIdFromThread && threadQuery.error) ? (
              <ErrorState
                error={reservationFromThreadQuery.error || threadQuery.error}
                onRetry={
                  reservationFromThreadQuery.error
                    ? reservationFromThreadQuery.refetch
                    : threadQuery.refetch
                }
              />
            ) : (
              <ReservationSidebar
                reservation={reservationFromThreadQuery.data as ReservationSidebarReservationProp}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  // --- Base /messages route: resolve a default selection ---

  // A calendar-bar click that resolved to "no thread yet" (calendarSelection)
  // already knows exactly which reservation to show, and doesn't depend on
  // listQuery (mostRecentThreadId / its own loading-error state) or on
  // reservationsQuery's OWN loading/error state to render it - a further
  // Codex PR review caught that gating this behind those checks meant a
  // slow or failed conversation-list request could delay this selection
  // indefinitely (hanging) or hide it entirely (error), even though nothing
  // about showing an already-resolved reservation actually depends on that
  // unrelated query succeeding. Render it immediately, ahead of every guard
  // below. Re-derives from reservationsQuery's data by ID (Codex PR review)
  // instead of permanently trusting the object captured at click time -
  // otherwise "Sync reservations" refetching that query would have no
  // visible effect on this sidebar. Falls back to the originally-clicked
  // object until refreshed data is available, so the sidebar is never
  // empty in the meantime.
  if (calendarSelection) {
    const calendarSelectionId = getReservationIdForLookup(calendarSelection)
    const refreshedCalendarSelection =
      reservationsQuery.data?.reservations?.find(
        r => getReservationIdForLookup(r) === calendarSelectionId
      ) ?? calendarSelection

    return (
      <div className={PAGE_ROOT_CLASS}>
        {pageHeader}
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[320px_1fr_320px]">
          <div className="overflow-hidden rounded-card border border-border bg-surface">
            <ConversationList
              propertyId={propertyId}
              selectedThreadId={null}
              onSelectThread={handleSelectThread}
            />
          </div>
          <div className="flex flex-col overflow-hidden rounded-card border border-border bg-surface">
            <ThreadHeader
              reservation={refreshedCalendarSelection as ThreadHeaderReservationProp}
              guestDisplayName={getGuestDisplayName(
                refreshedCalendarSelection as GuestDisplayNameArg
              )}
            />
            <div className="flex-1 overflow-y-auto p-4">
              <ThreadPane
                messages={[]}
                guestDisplayName={getGuestDisplayName(
                  refreshedCalendarSelection as GuestDisplayNameArg
                )}
              />
            </div>
          </div>
          <div className="overflow-y-auto rounded-card border border-border bg-surface p-4">
            <ReservationSidebar
              reservation={refreshedCalendarSelection as ReservationSidebarReservationProp}
              showNoThreadAction
            />
          </div>
        </div>
      </div>
    )
  }

  if (listQuery.isLoading) {
    return (
      <div className={PAGE_ROOT_CLASS}>
        {pageHeader}
        <FullPageSkeleton />
      </div>
    )
  }

  if (listQuery.error && listItems.length === 0) {
    return (
      <div className={PAGE_ROOT_CLASS}>
        {pageHeader}
        <ErrorState error={listQuery.error} onRetry={listQuery.refetch} />
      </div>
    )
  }

  if (mostRecentThreadId) {
    return <Navigate to={`/properties/${propertyId}/messages/${mostRecentThreadId}`} replace />
  }

  // No threads at all - fall back to the most recent/upcoming reservation
  // (or the true empty-property state if there isn't one either).
  if (reservationsQuery.isLoading) {
    return (
      <div className={PAGE_ROOT_CLASS}>
        {pageHeader}
        <FullPageSkeleton />
      </div>
    )
  }

  if (reservationsQuery.error) {
    return (
      <div className={PAGE_ROOT_CLASS}>
        {pageHeader}
        <ErrorState error={reservationsQuery.error} onRetry={reservationsQuery.refetch} />
      </div>
    )
  }

  const defaultReservation = pickDefaultReservation(reservationsQuery.data?.reservations)

  if (!defaultReservation) {
    return (
      <div className={PAGE_ROOT_CLASS}>
        {pageHeader}
        <EmptyState message="No reservations or messages yet for this property." />
      </div>
    )
  }

  return (
    <div className={PAGE_ROOT_CLASS}>
      {pageHeader}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[320px_1fr_320px]">
        <div className="overflow-hidden rounded-card border border-border bg-surface">
          <ConversationList
            propertyId={propertyId}
            selectedThreadId={null}
            onSelectThread={handleSelectThread}
          />
        </div>
        <div className="flex flex-col overflow-hidden rounded-card border border-border bg-surface">
          <ThreadHeader
            reservation={defaultReservation as ThreadHeaderReservationProp}
            guestDisplayName={getGuestDisplayName(defaultReservation as GuestDisplayNameArg)}
          />
          <div className="flex-1 overflow-y-auto p-4">
            <ThreadPane
              messages={[]}
              guestDisplayName={getGuestDisplayName(defaultReservation as GuestDisplayNameArg)}
            />
          </div>
        </div>
        <div className="overflow-y-auto rounded-card border border-border bg-surface p-4">
          {/* calendarSelection is handled by its own early return above -
              this is always the plain pickDefaultReservation() fallback. */}
          <ReservationSidebar
            reservation={defaultReservation as ReservationSidebarReservationProp}
          />
        </div>
      </div>
    </div>
  )
}
